'use client';

import { EventCard } from '@/sanity/fetchEvents';
import HIHLink from './Link';
import HIHImage from './HIHImage';
import { CategoryTag } from './Tag';
import { CategoryLink } from '@/sanity/fetchCategories';
import EventFacts from '../sections/EventFacts';
import { URL_PATH_EVENT } from '@/shared/constants';
import ActionFavorite from '../action/favorite/ActionFavorite';
import {
  MATOMO_CONTENT_INTERACTIONS,
  MATOMO_CONTENT_KINDS,
  buildContentPiece,
} from '@/utils/matomo';
import { useContentImpression } from '@/hooks/useContentImpression';
import { ContentBlockProvider } from './ContentTracking';
import styles from './SingleEventCard.module.scss';

interface SingleEventCardProps {
  event: EventCard;
  variant?: 'primary' | 'secondary';
}

export const SingleEventCard = ({ event, variant = 'primary' }: SingleEventCardProps) => {
  const eventPath = `/${URL_PATH_EVENT}/${event.slug.current}`;

  // Trackt nur innerhalb eines ContentTrackingModule, sonst No-Op – siehe ContentTracking.
  const { ref, block, trackInteraction } = useContentImpression({
    contentPiece: buildContentPiece(MATOMO_CONTENT_KINDS.EVENT, event.slug.current),
    contentTarget: eventPath,
  });

  const handleOpenClick = () =>
    trackInteraction(MATOMO_CONTENT_INTERACTIONS.DETAILSEITE_GEOEFFNET);

  return (
    <ContentBlockProvider block={block}>
      <div ref={ref} className={styles.singleEventCard}>
        <div
          className={[
            styles.imageWrapper,
            variant === 'primary' ? styles.primaryImage : styles.secondaryImage,
          ].join(' ')}
        >
          <HIHLink
            href={eventPath}
            onClick={handleOpenClick}
            className={styles.imageLink}
          >
            <HIHImage
              className={styles.image}
              image={event.coverImage}
              fill
              sizes="(min-width: 1024px) 33vw, 100vw"
            />
          </HIHLink>
          <ActionFavorite
            id={event._id}
            className={styles.favoriteButton}
            bookmarkType="Event"
          />
        </div>
        <div
          className={[
            styles.bottomHalf,
            variant === 'primary' ? styles.primaryBottom : styles.secondaryBottom,
          ].join(' ')}
        >
          <div>
            <div className={styles.tags}>
              {event.categories?.map((category: CategoryLink) => (
                <CategoryTag key={category._id} category={category} />
              ))}
            </div>
            <HIHLink href={eventPath} onClick={handleOpenClick}>
              <div className={styles.title}>{event.title}</div>
            </HIHLink>
          </div>
          <EventFacts event={event} className={styles.eventFacts} />
        </div>
      </div>
    </ContentBlockProvider>
  );
};
